import { type Product } from "@fitness-app/data-models/entities/Product";
import { type ProductStats } from "@fitness-app/data-models/entities/ProductStats";
import { getErrorMessage } from "@fitness-app/utils";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { getTeamOwnerId } from "../../../helpers/getTeamOwnerId";
import { type AppThunk } from "../../../index";
import {
  addProductToList,
  removeProductFromList,
  subscribeToProductsFailed,
  subscribeToProductsStarted,
  subscribeToProductsSuccess,
  updateProductOnList,
  updateProductStatsOnList,
} from "../reducer";
import { fetchProducts } from "./fetchProducts";
import { unsubscribeFromProducts } from "./unsubscribeFromProducts";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToProducts =
  (): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "products");

    try {
      dispatch(subscribeToProductsStarted());

      void dispatch(fetchProducts());

      const currentUser = await getLoggedUser(auth);
      const ownerId = getTeamOwnerId(currentUser);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "products", claimedSlot)) {
        return;
      }

      const channelName = `products-list-${currentUser.id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeProductsListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "products", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.productsListChannelName = channelName;
      subscriptions.realtimeProductsListChannel = channel;

      subscriptions.realtimeProductsListChannel
        .on<Product>(
          "postgres_changes",
          { event: "*", schema: "public", table: "product", filter: `owner=eq.${ownerId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductOnList(payload.new));
            }
            if (payload.eventType === "INSERT") {
              dispatch(addProductToList(payload.new));
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(removeProductFromList(payload.old.id));
            }
          },
        )
        .on<ProductStats>(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "product_stats" },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductStatsOnList({ product_stats: payload.new, id: payload.new.productId }));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToProductsSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromProducts());
          }
        });
    } catch (error) {
      dispatch(subscribeToProductsFailed(getErrorMessage(error)));
    }
  };
